import { Capacitor } from '@capacitor/core';
import { Order, OrderItem } from './dataService';
import { resolveReceiptInvoiceNumber, resolveReceiptOrderNumber } from './saleSerials';

// Declare global window interface for Cordova plugins
declare global {
  interface Window {
    bluetoothSerial: any;
  }
}

// ESC/POS Commands for thermal printer
const ESC = '\x1B';
const GS = '\x1D';

// Printer Commands
const COMMANDS = {
  INIT: ESC + '@',
  LINE_FEED: '\n',
  ALIGN_LEFT: ESC + 'a0',
  ALIGN_CENTER: ESC + 'a1',
  ALIGN_RIGHT: ESC + 'a2',
  FONT_SMALL: ESC + '!0',
  FONT_NORMAL: ESC + '!1',
  FONT_LARGE: ESC + '!16',
  FONT_BOLD: ESC + 'E1',
  FONT_BOLD_OFF: ESC + 'E0',
  UNDERLINE_ON: ESC + '-1',
  UNDERLINE_OFF: ESC + '-0',
  CUT_PAPER: GS + 'V1', // Partial cut
  DRAWER_OPEN: ESC + 'p048', // Open cash drawer
  BARCODE_HEIGHT: GS + 'h100',
  BARCODE_WIDTH: GS + 'w2',
  PRINT_BARCODE: GS + 'kA',
  DOUBLE_WIDTH: ESC + '!32',
  DOUBLE_HEIGHT: ESC + '!16',
};

export interface PrinterDevice {
  id: string;
  name: string;
  address: string;
  class?: number;
}

export class BluetoothPrinterService {
  private isConnected = false;
  private connectedDevice: PrinterDevice | null = null;

  constructor() {
    // Constructor for initialization
  }

  /**
   * Get the bluetoothSerial plugin instance
   */
  private getBluetoothSerial() {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Bluetooth is only available on native platforms');
    }
    
    if (!window.bluetoothSerial) {
      throw new Error('Bluetooth Serial plugin not available. Make sure you are running on a device with the plugin installed.');
    }
    
    return window.bluetoothSerial;
  }

  /**
   * Check if the device supports Bluetooth
   */
  async isBluetoothSupported(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Bluetooth printing is only available on native platforms');
      return false;
    }
    
    try {
      const bluetoothSerial = this.getBluetoothSerial();
      console.log('🔵 Checking if Bluetooth plugin is available...');
      
      return new Promise((resolve) => {
        bluetoothSerial.isEnabled(
          () => {
            console.log('✅ Bluetooth is supported and enabled');
            resolve(true);
          },
          () => {
            console.log('🔵 Bluetooth is supported but not enabled');
            resolve(true); // Hardware exists, just not enabled
          }
        );
      });
    } catch (error) {
      console.error('❌ Error checking Bluetooth support:', error);
      return false;
    }
  }

  /**
   * Enable Bluetooth if not already enabled
   */
  async enableBluetooth(): Promise<boolean> {
    try {
      const bluetoothSerial = this.getBluetoothSerial();
      console.log('🔵 Attempting to enable Bluetooth...');
      
      return new Promise((resolve) => {
        bluetoothSerial.enable(
          () => {
            console.log('✅ Bluetooth enabled successfully');
            resolve(true);
          },
          (error: any) => {
            console.error('❌ Error enabling Bluetooth:', error);
            resolve(false);
          }
        );
      });
    } catch (error) {
      console.error('❌ Error enabling Bluetooth:', error);
      return false;
    }
  }

  /**
   * Scan for available Bluetooth devices
   */
  async scanForDevices(): Promise<PrinterDevice[]> {
    try {
      console.log('🔍 Starting Bluetooth device scan...');
      
      const bluetoothSerial = this.getBluetoothSerial();

      // Try to enable Bluetooth if not enabled
      try {
        console.log('🔵 Checking if Bluetooth is enabled...');
        
        const isEnabled = await new Promise<boolean>((resolve) => {
          bluetoothSerial.isEnabled(
            () => resolve(true),
            () => resolve(false)
          );
        });
        
        console.log('🔵 Bluetooth enabled status:', isEnabled);
        
        if (!isEnabled) {
          console.log('🔵 Bluetooth not enabled, attempting to enable...');
          const enabled = await this.enableBluetooth();
          if (!enabled) {
            throw new Error('Failed to enable Bluetooth');
          }
          
          // Wait a moment for Bluetooth to initialize
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (enableError) {
        console.warn('⚠️ Could not check/enable Bluetooth, continuing with scan:', enableError);
        // Continue anyway, as some devices may still work
      }

      console.log('📱 Getting paired devices...');
      
      // Get paired devices
      const pairedDevices = await new Promise<any[]>((resolve, reject) => {
        bluetoothSerial.list(
          (devices: any[]) => {
            console.log('📱 Paired devices:', devices);
            resolve(devices || []);
          },
          (error: any) => {
            console.error('❌ Error getting paired devices:', error);
            reject(error);
          }
        );
      });
      
      let allDevices = [...pairedDevices];
      
      // Try to discover unpaired devices
      try {
        console.log('🔍 Starting discovery for unpaired devices...');
        
        await new Promise<void>((resolve, reject) => {
          bluetoothSerial.discoverUnpaired(
            () => {
              console.log('✅ Discovery started successfully');
              resolve();
            },
            (error: any) => {
              console.warn('⚠️ Discovery failed:', error);
              resolve(); // Don't fail the entire process
            }
          );
        });
        
        // Wait for discovery to complete
        await new Promise(resolve => setTimeout(resolve, 12000)); // Longer wait for discovery
        
        console.log('📱 Getting discovered devices...');
        
        // Get list of unpaired devices discovered
        const unpairedDevices = await new Promise<any[]>((resolve) => {
          bluetoothSerial.listUnpaired(
            (devices: any[]) => {
              console.log('📱 Unpaired devices:', devices);
              resolve(devices || []);
            },
            (error: any) => {
              console.warn('⚠️ Error getting unpaired devices:', error);
              resolve([]);
            }
          );
        });
        
        // Add unpaired devices to the list
        allDevices = [...allDevices, ...unpairedDevices];
        
      } catch (discoveryError) {
        console.warn('⚠️ Discovery failed, using only paired devices:', discoveryError);
        // Continue with just paired devices
      }
      
      console.log('📱 All devices found:', allDevices);
      
      // Format devices consistently
      const formattedDevices = allDevices.map((device: any) => ({
        id: device.id || device.address || `device-${Math.random()}`,
        name: device.name || 'Unknown Device',
        address: device.address || device.id,
        class: device.class || 0
      }));

      // Remove duplicates based on address
      const uniqueDevices = formattedDevices.filter((device, index, self) => 
        index === self.findIndex(d => d.address === device.address)
      );

      console.log(`✅ Found ${uniqueDevices.length} unique devices:`, uniqueDevices);
      return uniqueDevices;
      
    } catch (error) {
      console.error('❌ Error scanning for devices:', error);
      throw new Error(`Failed to scan for devices: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Connect to a specific device
   */
  async connectToDevice(device: PrinterDevice): Promise<boolean> {
    try {
      const bluetoothSerial = this.getBluetoothSerial();
      console.log('🔗 Attempting to connect to device:', device);
      
      return new Promise((resolve, reject) => {
        bluetoothSerial.connect(
          device.address,
          () => {
            this.isConnected = true;
            this.connectedDevice = device;
            console.log('✅ Successfully connected to device:', device.name);
            resolve(true);
          },
          (error: any) => {
            console.error('❌ Error connecting to device:', error);
            reject(new Error(`Failed to connect to ${device.name}: ${error}`));
          }
        );
      });
    } catch (error) {
      console.error('❌ Error connecting to device:', error);
      throw new Error(`Failed to connect to ${device.name}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Disconnect from the current device
   */
  async disconnect(): Promise<void> {
    try {
      if (this.isConnected) {
        const bluetoothSerial = this.getBluetoothSerial();
        
        return new Promise((resolve, reject) => {
          bluetoothSerial.disconnect(
            () => {
              this.isConnected = false;
              this.connectedDevice = null;
              console.log('✅ Disconnected from Bluetooth device');
              resolve();
            },
            (error: any) => {
              console.error('❌ Error disconnecting:', error);
              reject(new Error(`Failed to disconnect: ${error}`));
            }
          );
        });
      }
    } catch (error) {
      console.error('❌ Error disconnecting:', error);
      throw new Error(`Failed to disconnect: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): { isConnected: boolean; device: PrinterDevice | null } {
    return {
      isConnected: this.isConnected,
      device: this.connectedDevice
    };
  }

  /**
   * Send data to the connected printer
   */
  private async sendToPrinter(data: string): Promise<void> {
    if (!this.isConnected || !this.connectedDevice) {
      throw new Error('No printer connected. Please connect to a printer first.');
    }

    try {
      const bluetoothSerial = this.getBluetoothSerial();
      console.log('📤 Sending data to printer...');
      
      return new Promise((resolve, reject) => {
        bluetoothSerial.write(
          data,
          () => {
            console.log('✅ Data sent successfully');
            resolve();
          },
          (error: any) => {
            console.error('❌ Error sending data to printer:', error);
            reject(new Error(`Failed to send data to printer: ${error}`));
          }
        );
      });
    } catch (error) {
      console.error('❌ Error sending data to printer:', error);
      throw new Error(`Failed to send data to printer: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Generate receipt content in ESC/POS format (WhatsApp style)
   */
  private generateReceiptContent(order: Order, restaurantName: string = 'Cafelawn'): string {
    let receipt = '';

    // Initialize printer
    receipt += COMMANDS.INIT;
    receipt += COMMANDS.FONT_SMALL; // Set default small font
    
    // Header with compact styling
    receipt += COMMANDS.ALIGN_CENTER;
    receipt += COMMANDS.FONT_NORMAL;
    receipt += COMMANDS.FONT_BOLD;
    receipt += `☕ ${restaurantName.toUpperCase()} ☕` + COMMANDS.LINE_FEED;
    receipt += COMMANDS.FONT_BOLD_OFF;
    receipt += COMMANDS.FONT_SMALL;
    receipt += 'Your Digital Receipt' + COMMANDS.LINE_FEED;
    
    // Compact separator
    receipt += '◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆' + COMMANDS.LINE_FEED;
    
    // Order details with compact layout
    receipt += COMMANDS.ALIGN_LEFT;
    receipt += `📋 Order #${resolveReceiptOrderNumber(order)}` + COMMANDS.LINE_FEED;
    if (order.invoiceNumber) {
      receipt += `🧾 Bill #${order.invoiceNumber}` + COMMANDS.LINE_FEED;
    } else {
      receipt += `🧾 Bill #${resolveReceiptInvoiceNumber(order)}` + COMMANDS.LINE_FEED;
    }
    receipt += `🪑 Table ${order.tableNumber || 'Takeaway'}` + COMMANDS.LINE_FEED;
    
    const orderDate = order.createdAt ? order.createdAt : new Date();
    const timeString = orderDate.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    const dateString = orderDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
    receipt += `🕐 ${timeString} • ${dateString}` + COMMANDS.LINE_FEED;
    
    // Items section with compact design
    receipt += COMMANDS.ALIGN_CENTER;
    receipt += COMMANDS.FONT_BOLD;
    receipt += '━━━━━━━━━ YOUR ORDER ━━━━━━━━━' + COMMANDS.LINE_FEED;
    receipt += COMMANDS.FONT_BOLD_OFF;
    
    // Items list
    receipt += COMMANDS.ALIGN_LEFT;
    order.items.forEach((item: OrderItem, index: number) => {
      // Item with compact formatting
      receipt += `${index + 1}. ${item.menuItemName}` + COMMANDS.LINE_FEED;
      receipt += `   ${item.quantity}x @ ₹${item.unitPrice.toFixed(2)} = ₹${item.totalPrice.toFixed(2)}` + COMMANDS.LINE_FEED;
      
      // Add notes if any
      if (item.notes && item.notes.trim()) {
        receipt += `   💬 ${item.notes.trim()}` + COMMANDS.LINE_FEED;
      }
    });
    
    // Bill summary section
    receipt += COMMANDS.ALIGN_CENTER;
    receipt += '┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈ ┈' + COMMANDS.LINE_FEED;
    
    // Subtotal and total
    receipt += COMMANDS.ALIGN_LEFT;
    receipt += this.padString('Subtotal:', 20) + `₹${order.total.toFixed(2)}` + COMMANDS.LINE_FEED;
    
    // Payment status
    if (order.paidAmount > 0) {
      receipt += this.padString('💳 Amount Paid:', 20) + `₹${order.paidAmount.toFixed(2)}` + COMMANDS.LINE_FEED;
      
      if (order.remainingAmount && order.remainingAmount > 0) {
        receipt += this.padString('⏳ Remaining:', 20) + `₹${order.remainingAmount.toFixed(2)}` + COMMANDS.LINE_FEED;
      } else {
        receipt += COMMANDS.ALIGN_CENTER;
        receipt += COMMANDS.FONT_BOLD;
        receipt += '✅ FULLY PAID ✅' + COMMANDS.LINE_FEED;
        receipt += COMMANDS.FONT_BOLD_OFF;
        receipt += COMMANDS.ALIGN_LEFT;
      }
    }
    
    // Grand total with compact emphasis
    receipt += COMMANDS.ALIGN_CENTER;
    receipt += COMMANDS.FONT_BOLD;
    receipt += `💰 TOTAL: ₹${order.total.toFixed(2)}` + COMMANDS.LINE_FEED;
    receipt += COMMANDS.FONT_BOLD_OFF;
    
    // Compact thank you section (single line)
    receipt += '◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆' + COMMANDS.LINE_FEED;
    receipt += COMMANDS.FONT_BOLD;
    receipt += '🙏 THANK YOU! Visit again! 🔄' + COMMANDS.LINE_FEED;
    receipt += COMMANDS.FONT_BOLD_OFF;
    
    // Compact contact info
    receipt += '📞 Contact: Your Phone Number' + COMMANDS.LINE_FEED;
    receipt += 'Powered by Cafelawn POS' + COMMANDS.LINE_FEED;
    
    // Cut paper
    receipt += COMMANDS.CUT_PAPER;
    
    return receipt;
  }

  /**
   * Helper function to pad strings for alignment
   */
  private padString(str: string, length: number): string {
    if (str.length >= length) return str.substring(0, length);
    return str + ' '.repeat(length - str.length);
  }

  /**
   * Print a receipt for the given order
   */
  async printReceipt(order: Order, restaurantName?: string): Promise<void> {
    if (!this.isConnected || !this.connectedDevice) {
      throw new Error('No printer connected. Please connect to a printer first.');
    }

    try {
      const receiptContent = this.generateReceiptContent(order, restaurantName);
      await this.sendToPrinter(receiptContent);
      console.log('✅ Receipt printed successfully');
    } catch (error) {
      console.error('❌ Error printing receipt:', error);
      throw new Error(`Failed to print receipt: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Print a test page to verify printer connection
   */
  async printTestPage(): Promise<void> {
    if (!this.isConnected || !this.connectedDevice) {
      throw new Error('No printer connected. Please connect to a printer first.');
    }

    try {
      let testContent = '';
      testContent += COMMANDS.INIT;
      testContent += COMMANDS.ALIGN_CENTER;
      testContent += COMMANDS.FONT_LARGE;
      testContent += COMMANDS.FONT_BOLD;
      testContent += 'TEST PRINT' + COMMANDS.LINE_FEED;
      testContent += COMMANDS.FONT_BOLD_OFF;
      testContent += COMMANDS.FONT_NORMAL;
      testContent += '================' + COMMANDS.LINE_FEED;
      testContent += 'Printer Connected Successfully!' + COMMANDS.LINE_FEED;
      testContent += `Time: ${new Date().toLocaleString()}` + COMMANDS.LINE_FEED;
      testContent += `Device: ${this.connectedDevice.name}` + COMMANDS.LINE_FEED;
      testContent += '================' + COMMANDS.LINE_FEED;
      testContent += COMMANDS.LINE_FEED;
      testContent += COMMANDS.LINE_FEED;
      testContent += COMMANDS.CUT_PAPER;

      await this.sendToPrinter(testContent);
      console.log('✅ Test page printed successfully');
    } catch (error) {
      console.error('❌ Error printing test page:', error);
      throw new Error(`Failed to print test page: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Open cash drawer (if connected)
   */
  async openCashDrawer(): Promise<void> {
    if (!this.isConnected || !this.connectedDevice) {
      throw new Error('No printer connected. Please connect to a printer first.');
    }

    try {
      await this.sendToPrinter(COMMANDS.DRAWER_OPEN);
      console.log('✅ Cash drawer opened successfully');
    } catch (error) {
      console.error('❌ Error opening cash drawer:', error);
      throw new Error(`Failed to open cash drawer: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}

// Export singleton instance
export const bluetoothPrinter = new BluetoothPrinterService(); 